import {
  useGetTrademarkCardQuery,
  usePostTrademarkActivateMutation,
  usePostTrademarkArchiveMutation,
  usePostTrademarkRegistrationDeleteMutation,
  usePostTrademarkUploadLetterMutation,
} from '../../../../app/api'
import { Square } from '../../../../shared/components/utility/square/Square'
import { Link, useParams } from 'react-router-dom'
import { IconButton } from '../../../../shared/components/buttons/icon-button/IconButton'
import { setModal } from '../../../../app/store/reducers/AppSlice'
import { EditOwnerNameModal } from './EditOwnerNameModal'
import { IconEdit } from '../../../../shared/components/icons/IconEdit'
import { EditOwnerWebsiteModal } from './EditOwnerWebsiteModal'
import { BpButton } from '../../../../shared/components/buttons/bp-button/BpButton'
import { IconPlus } from '../../../../shared/components/icons/IconPlus'
import { AddTrademarkRegistrationModal } from '../../../../lib/widgets/add-trademark-registration-modal/AddTrademarkRegistrationModal'
import { cellClass, Table, TableCell } from '../../../../shared/ui/table/Table'
import { EditTrademarkRegistrationModal } from './EditTrademarkRegistrationModal'
import { IconTrash } from '../../../../shared/components/icons/IconTrash'
import { PdfFile } from '../../../../shared/ui/illustrations/pdf-file'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { DropOptions } from '../../../../shared/components/forms/drop-options/DropOptions'
import styles from '../affiliate-rule/ProjectAffiliateRule.module.scss'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import clsx from 'clsx'

interface TrademarkCardProps {
  projectId: number
  trademarkId: number
}

export const TrademarkCard = ({ projectId, trademarkId }: TrademarkCardProps) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const dispatch = useAppDispatch()

  const { data, isLoading } = useGetTrademarkCardQuery({
    projectId: projectId,
    trademarkId: trademarkId,
  })
  const [activateTrademark] = usePostTrademarkActivateMutation()
  const [archiveTrademark] = usePostTrademarkArchiveMutation()
  const [deleteTrademarkRegistration, { isLoading: deleteTrademarkRegistrationLoading }] =
    usePostTrademarkRegistrationDeleteMutation()
  const [uploadAuthorisationLetter, { isLoading: uploadAuthorisationLetterLoading }] =
    usePostTrademarkUploadLetterMutation()

  const authorisationLetterInputRef = React.useRef<HTMLInputElement>(null)

  const uploadLetter = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    event.target.value = ''

    if (!file) return

    uploadAuthorisationLetter({
      body: {
        project_id: projectId,
        trademark_id: trademarkId,
        authorisation_letter: file,
      },
    })
  }

  const HEADERS = [
    { label: t('table.columns.country'), width: '250px' },
    { label: t('table.columns.registrationNumber') },
    { label: '' },
  ]

  if (isLoading) {
    return (
      <Square className={'relative mt-[32px] h-[175px] w-full px-[22px] py-[24px]'}>
        <Preloader />
      </Square>
    )
  }

  return (
    <div className={'mt-[32px] flex flex-col gap-[16px]'}>
      <Square className={'flex flex-col gap-[24px] py-[24px] px-[22px]'}>
        <div className={'flex items-center justify-between gap-[16px]'}>
          <div className={'flex items-center gap-[13px]'}>
            <h2>{data?.data?.name}</h2>
            <DropOptions
              options={[
                {
                  text: t('pages.project.activate'),
                  value: 'active',
                  hidden: data?.data?.status === 'active',
                },
                {
                  text: t('pages.project.deactivate'),
                  value: 'inactive',
                  hidden: data?.data?.status === 'inactive',
                },
              ]}
              onChange={(e) => {
                if (!data?.data) return

                if (data?.data?.status === 'active') {
                  archiveTrademark({
                    body: {
                      project_id: data.data.project_id,
                      trademark_id: data.data.id,
                    },
                  })
                } else {
                  activateTrademark({
                    body: {
                      project_id: data.data.project_id,
                      trademark_id: data.data.id,
                    },
                  })
                }
              }}
            >
              {data?.data?.status === 'active' ? (
                <span className={styles.badgeActive}>{t('pages.project.active')}</span>
              ) : (
                <span className={styles.badgeInactive}>{t('pages.project.inactive')}</span>
              )}
            </DropOptions>
          </div>
          <Link
            className={clsx('text-[15px] text-focus', {
              ['!pointer-events-none !text-aqua-300']:
                !data?.data?.infringements_count || data?.data?.status !== 'active',
            })}
            to={`/tm-infringement?project=${id}`}
          >
            {data?.data?.infringements_count} {t('pages.project.infringementsFound')}
          </Link>
        </div>
        <div className={'flex flex-col gap-[16px] lg:flex-row'}>
          <div className={'flex w-[348px] flex-col gap-[8px]'}>
            <h4 className={'text-[13px] font-medium leading-[18px]'}>
              {t('pages.project.trademarkOwnerName')}
            </h4>
            <div
              className={
                'flex items-center justify-between gap-[16px] rounded-[6px] bg-focus/[0.05] p-[12px]'
              }
            >
              <p className={'pl-[4px] text-focus-500'}>{data?.data?.owner}</p>
              <IconButton
                size='lg'
                onClick={() => {
                  if (!data?.data) return

                  dispatch(
                    setModal({
                      children: (
                        <EditOwnerNameModal
                          data={{
                            project_id: data.data.project_id,
                            trademark_id: data.data.id,
                            owner: data.data.owner as string,
                          }}
                        />
                      ),
                    }),
                  )
                }}
              >
                <IconEdit color='#A059FF' />
              </IconButton>
            </div>
          </div>
          <div className={'flex w-[348px] flex-col gap-[8px]'}>
            <h4 className={'text-[13px] font-medium leading-[18px]'}>
              {t('pages.project.trademarkOwnerWebsite')}
            </h4>
            <div
              className={
                'flex items-center justify-between gap-[16px] rounded-[6px] bg-focus/[0.05] p-[12px]'
              }
            >
              <p className={'pl-[4px] text-focus-500'}>{data?.data?.owner_website}</p>
              <IconButton
                size='lg'
                onClick={() => {
                  if (!data?.data) return

                  dispatch(
                    setModal({
                      children: (
                        <EditOwnerWebsiteModal
                          data={{
                            project_id: data.data.project_id,
                            trademark_id: data.data.id,
                            owner_website: data.data.owner_website as string,
                          }}
                        />
                      ),
                    }),
                  )
                }}
              >
                <IconEdit color='#A059FF' />
              </IconButton>
            </div>
          </div>
        </div>
        <div className={'flex flex-col gap-[16px]'}>
          <div className={'flex items-center justify-between gap-[16px]'}>
            <h3 className={'text-[18px] font-semibold leading-[24px]'}>
              {data?.data?.registrations.length} {t('pages.project.registrations')}
            </h3>
            <BpButton
              className={'uppercase'}
              iconLeft={<IconPlus />}
              theme={'outline'}
              onClick={() => {
                if (!data?.data) return

                dispatch(
                  setModal({
                    children: (
                      <AddTrademarkRegistrationModal
                        data={{
                          project_id: data.data.project_id,
                          trademark_id: data.data.id,
                        }}
                      />
                    ),
                  }),
                )
              }}
            >
              {t('pages.project.addRegistration')}
            </BpButton>
          </div>
          <div className={'w-full overflow-hidden rounded-[16px] border border-[#E5E1EA]'}>
            <Table headers={HEADERS} isEmpty={false}>
              {data?.data?.registrations.map((registration, index) => (
                <tr key={index} className={'relative'}>
                  <TableCell className={cellClass}>{registration.country}</TableCell>
                  <TableCell className={cellClass}>{registration.registration_number}</TableCell>
                  <TableCell>
                    <div className={'flex items-center justify-end gap-[8px]'}>
                      <IconButton
                        size='lg'
                        onClick={() => {
                          if (!data?.data) return

                          dispatch(
                            setModal({
                              children: (
                                <EditTrademarkRegistrationModal
                                  data={{
                                    project_id: data.data.project_id,
                                    trademark_id: data.data.id,
                                    registration_id: registration.id as number,
                                    country_id: registration.country_id,
                                    registration_number: registration.registration_number,
                                  }}
                                />
                              ),
                            }),
                          )
                        }}
                      >
                        <IconEdit color='#A059FF' />
                      </IconButton>
                      <IconButton
                        disabled={deleteTrademarkRegistrationLoading}
                        size='lg'
                        onClick={() => {
                          deleteTrademarkRegistration({
                            body: {
                              project_id: projectId,
                              trademark_id: trademarkId,
                              registration_id: registration.id as number,
                            },
                          })
                        }}
                      >
                        <IconTrash color='#FF2450' />
                      </IconButton>
                    </div>
                  </TableCell>
                </tr>
              ))}
            </Table>
          </div>
        </div>
        <div className={'flex w-full max-w-[600px] flex-col gap-[8px]'}>
          <h4 className={'text-[13px] font-medium leading-[18px]'}>
            {t('pages.project.authorizationLetter')}
          </h4>
          <div
            className={
              'flex items-center justify-between gap-[16px] rounded-[6px] border border-[#E5E1EA] py-[12px] pl-[16px] pr-[8px]'
            }
          >
            <div className={'flex items-center gap-[16px]'}>
              <div>
                <PdfFile />
              </div>
              <div>
                <p className={'text-[15px] leading-[24px] text-focus'}>
                  {data?.data?.authorisation_letter_name ||
                    data?.data?.authorisation_letter_url?.split('/').pop()}
                </p>
                {!!data?.data?.uploaded_at && (
                  <p className={'text-[13px] leading-[18px] text-[#8E7CA7]'}>
                    {t('pages.project.uploaded')} {data?.data?.uploaded_at}
                  </p>
                )}
              </div>
            </div>
            <IconButton
              className={'border-none shadow-none'}
              disabled={uploadAuthorisationLetterLoading}
              size='lg'
              onClick={() => authorisationLetterInputRef.current?.click()}
            >
              <IconEdit color='#A059FF' />
            </IconButton>
            <input
              ref={authorisationLetterInputRef}
              accept={'application/pdf'}
              className={'hidden'}
              type={'file'}
              onChange={uploadLetter}
            />
          </div>
        </div>
      </Square>
    </div>
  )
}
